import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import AssignmentsPage from "../assignments/AssignmentsPage.jsx";
import ConfirmForMatchingDialog from "./ConfirmForMatchingDialog.jsx";
import AiMatchingPanel from "./AiMatchingPanel.jsx";
import CopyResumeLinkButton from "./CopyResumeLinkButton.jsx";
import { listReadyForMatchCandidates, getReadyForMatchCandidate } from "../readyForMatch/readyForMatchApi.js";
import { listJobs } from "../thailandJobs/thailandJobsApi.js";
import { listRecommendationJobs, listMatchingRecommendations } from "../matchingRecommendation/matchingRecommendationApi.js";
import {
  WORKSPACE_TABS,
  WORKSPACE_TAB_LABELS,
  WORKSPACE_STEPS,
  POOL_GROUPS,
  POOL_GROUP_META,
  classifyCandidate,
  employerDecisionOf,
  EMPLOYER_DECISIONS,
  EMPLOYER_DECISION_LABELS,
  initialsOf,
  maskPassport,
} from "./matchingWorkspaceModel.js";
import "./matchingWorkspace.css";

const PAGE_TITLE = "Matching Demand กับแรงงาน";
const PAGE_SUBTITLE = "คัดแรงงานที่พร้อม จับคู่กับงาน และส่งให้ SysAdmin อนุมัติ";
const POOL_PAGE_SIZE = 25;
const CRUMBS = ["Backoffice", "Thailand Jobs", "Matching Demand กับแรงงาน"];

/** The four routes that open this one screen, and the tab each opens on. */
const TAB_FOR_PATH = {
  "/backoffice/thailand-jobs/matching-queue": WORKSPACE_TABS.ReadyPool,
  "/backoffice/thailand-jobs/matching-recommendation": WORKSPACE_TABS.Recommendations,
  // P156: Final Approval had no URL of its own. PATH_FOR_TAB pointed it at the
  // Ready Pool path, which this map then resolved straight back to Ready Pool -
  // so the tab bounced to the first tab the instant it was clicked and its
  // content was unreachable.
  "/backoffice/thailand-jobs/matching-approved": WORKSPACE_TABS.Approved,
  "/backoffice/thailand-jobs/assignments": WORKSPACE_TABS.Assignments,
};

const PATH_FOR_TAB = {
  [WORKSPACE_TABS.ReadyPool]: "/backoffice/thailand-jobs/matching-queue",
  [WORKSPACE_TABS.Recommendations]: "/backoffice/thailand-jobs/matching-recommendation",
  [WORKSPACE_TABS.Approved]: "/backoffice/thailand-jobs/matching-approved",
  [WORKSPACE_TABS.Assignments]: "/backoffice/thailand-jobs/assignments",
};

export function MatchingWorkspacePage({ initialTab = WORKSPACE_TABS.ReadyPool }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Every legacy route lands on this page; the path decides the opening tab so
  // existing bookmarks still mean what they used to mean.
  useEffect(() => {
    setActiveTab(TAB_FOR_PATH[location.pathname] ?? initialTab);
  }, [location.pathname, initialTab]);

  const pool = useReadyPool();
  const jobs = useRecommendationJobs();
  const approvals = useApprovals();

  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  // The worker whose profile is being confirmed before entering matching, and
  // the ones confirmed in this sitting. The second exists because a confirmed
  // worker leaves the queue on the next read - without it the row would just
  // silently disappear, which reads like a bug rather than a completed step.
  const [confirmWorkerId, setConfirmWorkerId] = useState(null);
  const [justSentIds, setJustSentIds] = useState([]);

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-mw">
        <header className="wx-mw-head">
          <div>
            <h1 className="wx-mw-title">{PAGE_TITLE}</h1>
            <p className="wx-mw-subtitle">{PAGE_SUBTITLE}</p>
          </div>
        </header>

        <WorkspaceStepper activeTab={activeTab} />

        <KpiStrip pool={pool} jobs={jobs} approvals={approvals} />

        <nav className="wx-mw-tabs" role="tablist" aria-label="Matching Demand กับแรงงาน">
          {Object.values(WORKSPACE_TABS).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`wx-mw-tab${activeTab === tab ? " wx-mw-tab--active" : ""}`}
              onClick={() => {
                setActiveTab(tab);
                // Keep the URL honest so a refresh reopens the same view.
                navigate(PATH_FOR_TAB[tab] ?? PATH_FOR_TAB[WORKSPACE_TABS.ReadyPool], { replace: true });
              }}
            >
              {WORKSPACE_TAB_LABELS[tab]}
            </button>
          ))}
        </nav>

        {activeTab === WORKSPACE_TABS.ReadyPool && (
          <ReadyPoolTab
            pool={pool}
            onOpenWorker={setSelectedWorkerId}
            onConfirmWorker={setConfirmWorkerId}
            justSentIds={justSentIds}
          />
        )}
        {activeTab === WORKSPACE_TABS.Recommendations && <RecommendationsTab jobs={jobs} />}
        {activeTab === WORKSPACE_TABS.Approved && <AssignedTab approvals={approvals} />}
        {activeTab === WORKSPACE_TABS.Assignments && <OnboardTab approvals={approvals} />}

        {/* One worker record for the whole app - the same drawer
            /backoffice/agent/workers opens. */}
        <WorkerRecordDrawer
          open={selectedWorkerId !== null}
          workerId={selectedWorkerId}
          onClose={() => setSelectedWorkerId(null)}
          selectableSelector=".wx-mw-row-action"
        />

        {confirmWorkerId !== null && (
          <ConfirmForMatchingDialog
            workerId={confirmWorkerId}
            onClose={() => setConfirmWorkerId(null)}
            onConfirmed={(workerId) => {
              setJustSentIds((ids) => (ids.includes(workerId) ? ids : [...ids, workerId]));
              pool.refetch();
            }}
          />
        )}
      </div>
    </AppShell>
  );
}

// --- Data hooks: every one reads a real, backend-scoped endpoint ---

function useReadyPool() {
  const [state, setState] = useState({ status: "loading", items: [], totalCount: 0, error: null });
  // Confirming a worker removes them from this queue (it only ever lists the
  // not-yet-confirmed), so the list has to be re-read after one is sent.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, status: "loading", error: null }));
    listReadyForMatchCandidates({ page: 1, pageSize: POOL_PAGE_SIZE })
      .then((data) => {
        if (cancelled) return;
        setState({
          status: "ready",
          items: data?.items ?? [],
          totalCount: data?.totalCount ?? 0,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: "error", items: [], totalCount: 0, error });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}

function useRecommendationJobs() {
  const [state, setState] = useState({ status: "loading", items: [], totalCount: 0, error: null });

  useEffect(() => {
    let cancelled = false;
    listRecommendationJobs({ page: 1, pageSize: 25 })
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", items: data?.items ?? [], totalCount: data?.totalCount ?? 0, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: "error", items: [], totalCount: 0, error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * How many jobs exist AT ALL, ignoring whether any is ready to match.
 *
 * The recommendation queue only ever returns DemandStatus = ReadyForMatching,
 * so on its own it cannot tell "no jobs exist" from "jobs exist but none are
 * ready" - and those two need opposite instructions. pageSize 1: only
 * totalCount is used, so there is no reason to transfer a page of rows.
 */
function useAnyJobsExist() {
  const [state, setState] = useState({ status: "loading", totalCount: 0 });

  useEffect(() => {
    let cancelled = false;
    listJobs({ page: 1, pageSize: 1 })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", totalCount: data?.totalCount ?? 0 });
        }
      })
      .catch(() => {
        // If this probe fails, fall back to the older, vaguer wording rather
        // than asserting either cause - never guess which one it is.
        if (!cancelled) {
          setState({ status: "error", totalCount: 0 });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

function useApprovals() {
  const [state, setState] = useState({ status: "loading", items: [], totalCount: 0, error: null });

  useEffect(() => {
    let cancelled = false;
    listMatchingRecommendations({ page: 1, pageSize: 25 })
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", items: data?.items ?? [], totalCount: data?.totalCount ?? 0, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ status: "error", items: [], totalCount: 0, error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

// --- Header pieces ---

function WorkspaceStepper({ activeTab }) {
  return (
    <ol className="wx-mw-stepper" aria-label="ขั้นตอนการจับคู่งาน">
      {WORKSPACE_STEPS.map((step, index) => (
        <li
          key={step.key}
          className={`wx-mw-step${step.key === activeTab ? " wx-mw-step--current" : ""}`}
          aria-current={step.key === activeTab ? "step" : undefined}
        >
          <span className="wx-mw-step-index">{index + 1}</span>
          <span className="wx-mw-step-label">{step.label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Every figure is a count the API returned. Where a number is genuinely zero
 * the tile says why, because "0" with no reason is what made the old pages
 * unreadable.
 */
function KpiStrip({ pool, jobs, approvals }) {
  const grouped = useMemo(() => groupPool(pool.items), [pool.items]);

  const tiles = [
    {
      key: "ready",
      label: "พร้อมแนะนำงาน",
      value: grouped[POOL_GROUPS.Ready].length,
      note: `จากแรงงานทั้งหมด ${pool.totalCount} คนในขอบเขตของคุณ`,
    },
    {
      key: "awaiting-confirm",
      label: "รอยืนยัน",
      value: grouped[POOL_GROUPS.Incomplete].length,
      note: "ตรวจโปรไฟล์ครั้งสุดท้ายแล้วส่งเข้าจับคู่ได้",
    },
    {
      key: "blocked",
      label: "ข้อมูลยังไม่ครบ",
      // Only the two groups whose blocking gap the list payload actually
      // proves. The rest sit under "รอยืนยัน" - counting them as incomplete
      // would tell an operator to go fix data that is already there.
      value: grouped[POOL_GROUPS.AwaitingEkyc].length + grouped[POOL_GROUPS.NeedsProfile].length,
      note: "ยังจับคู่งานไม่ได้จนกว่าจะเติมข้อมูลที่ขาด",
    },
    {
      key: "jobs",
      label: "งานที่เปิดรับ",
      value: jobs.totalCount,
      note: jobs.totalCount === 0 ? "ยังไม่มีงานที่เปิดรับ จึงยังจับคู่ไม่ได้" : "งานที่พร้อมรับแรงงาน",
    },
    {
      key: "recommendations",
      label: "คู่ที่แนะนำ",
      value: jobs.totalCount === 0 ? 0 : null,
      note:
        jobs.totalCount === 0
          ? "ต้องมีงานเปิดรับก่อนจึงจะแนะนำคู่ได้"
          : "เลือกงานในแท็บ Recommendations เพื่อดูคู่ที่แนะนำ",
    },
    {
      key: "approved",
      label: "อนุมัติแล้ว",
      value: approvals.totalCount,
      note: approvals.totalCount === 0 ? "ยังไม่มีการอนุมัติในระบบ" : "ส่งให้ขั้นตอนถัดไปแล้ว",
    },
  ];

  return (
    <div className="wx-mw-kpis">
      {tiles.map((tile) => (
        <article key={tile.key} className="wx-mw-kpi">
          <p className="wx-mw-kpi-label">{tile.label}</p>
          <p className="wx-mw-kpi-value">{tile.value === null ? "—" : tile.value}</p>
          <p className="wx-mw-kpi-note">{tile.note}</p>
        </article>
      ))}
    </div>
  );
}

function groupPool(items) {
  const groups = {
    [POOL_GROUPS.Ready]: [],
    [POOL_GROUPS.Incomplete]: [],
    [POOL_GROUPS.AwaitingEkyc]: [],
    [POOL_GROUPS.NeedsProfile]: [],
  };
  for (const item of items ?? []) {
    groups[classifyCandidate(item)].push(item);
  }
  return groups;
}

// --- Ready Pool ---

function ReadyPoolTab({ pool, onOpenWorker, onConfirmWorker, justSentIds }) {
  const grouped = useMemo(() => groupPool(pool.items), [pool.items]);

  if (pool.status === "loading") {
    return <p className="wx-mw-muted">กำลังโหลดรายชื่อแรงงาน...</p>;
  }
  if (pool.status === "error") {
    return (
      <EmptyState
        title="โหลดรายชื่อแรงงานไม่สำเร็จ"
        why="ติดต่อระบบไม่ได้ หรือบัญชีของคุณไม่มีสิทธิ์เข้าถึงข้อมูลชุดนี้"
        next="ลองรีเฟรชหน้านี้อีกครั้ง ถ้ายังไม่ได้ให้แจ้งทีมส่วนกลาง"
      />
    );
  }
  if (pool.items.length === 0) {
    return (
      <EmptyState
        title="ยังไม่มีแรงงานในขอบเขตของคุณ"
        why="ยังไม่มีใครผ่าน Quick Register หรือคุณยังไม่ได้รับมอบหมายสายงานใด"
        next="เริ่มจากหน้า Invite / Applications เพื่อรับแรงงานเข้าระบบ"
        action={{ to: "/backoffice/worker-intake/applications", label: "ไปหน้าใบสมัคร" }}
      />
    );
  }

  return (
    <div className="wx-mw-pool">
      {/* What this tab is for, in one line. Everyone below is waiting on a
          person to confirm their profile - the queue only ever lists workers
          who have not been sent yet, so an empty tab genuinely means there is
          nothing to confirm. */}
      <p className="wx-mw-step-intro">
        ตรวจโปรไฟล์ครั้งสุดท้าย แล้วยืนยันส่งแรงงานเข้าระบบจับคู่งาน คนที่ยืนยันแล้วจะออกจากรายการนี้และไปรอในขั้นตอน Recommendations
        {justSentIds.length > 0 && (
          <strong className="wx-mw-step-sent"> ยืนยันไปแล้ว {justSentIds.length.toLocaleString("th-TH")} คนในรอบนี้</strong>
        )}
      </p>

      {Object.values(POOL_GROUPS).map((group) => {
        const rows = grouped[group];
        const meta = POOL_GROUP_META[group];
        return (
          <section key={group} className="wx-mw-group">
            <header className="wx-mw-group-head">
              <h2>
                <span className={`wx-mw-dot wx-mw-dot--${meta.tone}`} aria-hidden="true" />
                {meta.label}
                <span className="wx-mw-group-count">{rows.length}</span>
              </h2>
              <p>{meta.hint}</p>
            </header>

            {rows.length === 0 ? (
              <p className="wx-mw-group-empty">ไม่มีแรงงานในกลุ่มนี้</p>
            ) : (
              <table className="wx-mw-table">
                <thead>
                  <tr>
                    <th scope="col">แรงงาน</th>
                    <th scope="col">สัญชาติ / ภาษา</th>
                    <th scope="col">หนังสือเดินทาง</th>
                    <th scope="col">เอเย่นต์ / สายงาน</th>
                    <th scope="col">สถานะความพร้อม</th>
                    <th scope="col"><span className="wx-mw-sr">การทำงาน</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <PoolRow
                      key={row.workerId}
                      row={row}
                      onOpen={() => onOpenWorker(row.workerId)}
                      onConfirm={() => onConfirmWorker(row.workerId)}
                      justSent={justSentIds.includes(row.workerId)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
}

function PoolRow({ row, onOpen, onConfirm, justSent }) {
  const masked = maskPassport(row.passportNumber);
  // The list row cannot answer "may this worker be sent" - only the candidate
  // detail carries the readiness evaluation and the permission. So the button
  // opens the check rather than performing the action, and it is offered for
  // anyone still in the queue. A worker already sent in this sitting shows
  // what happened instead of an action that would now fail.
  const alreadySent = justSent || row.readyForMatch === true;
  return (
    <tr>
      <td>
        <div className="wx-mw-worker">
          <Avatar photoUrl={row.photoUrl} fullName={row.fullName} />
          <div>
            <strong>{row.fullName ?? "-"}</strong>
            <span>{row.workerCode ?? row.workerId}</span>
          </div>
        </div>
      </td>
      <td>
        {row.nationality ?? "-"}
        <span className="wx-mw-sub">{row.preferredLanguageCode ?? "-"}</span>
      </td>
      <td>
        {masked ? (
          <span className="wx-mw-mask">{masked}</span>
        ) : (
          <span className="wx-mw-sub">ยังไม่มีในระบบ</span>
        )}
      </td>
      <td>
        {row.agent?.agentCode ?? "-"}
        <span className="wx-mw-sub">{row.line?.displayName ?? row.agent?.displayName ?? "-"}</span>
      </td>
      <td>
        <ReadinessBadge candidate={row} />
      </td>
      <td>
        <div className="wx-mw-row-actions">
          <button type="button" className="wx-button wx-button--ghost wx-mw-row-action" onClick={onOpen}>
            ดูรายละเอียด
          </button>
          {alreadySent ? (
            <span className="wx-mw-row-sent">ส่งเข้าจับคู่แล้ว</span>
          ) : (
            <button type="button" className="wx-button wx-button--primary wx-mw-row-action wx-mw-row-confirm" onClick={onConfirm}>
              ยืนยันส่งเข้าจับคู่
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Avatar({ photoUrl, fullName }) {
  if (photoUrl) {
    return <img className="wx-mw-avatar" src={photoUrl} alt="" loading="lazy" />;
  }
  // No photo on file is common for Quick Registered workers - initials keep the
  // row scannable without pretending an image exists.
  return (
    <span className="wx-mw-avatar wx-mw-avatar--initials" aria-hidden="true">
      {initialsOf(fullName)}
    </span>
  );
}

function ReadinessBadge({ candidate }) {
  const readiness = candidate?.readiness;
  if (readiness?.statusLabel) {
    return (
      <span className={`wx-badge wx-badge--${readiness.readyEligibility ? "success" : "warning"}`}>
        {readiness.statusLabel}
      </span>
    );
  }
  // The list endpoint returns fewer fields than the detail one; say so rather
  // than inferring a status this row cannot support.
  return <span className="wx-mw-sub">ดูรายละเอียดเพื่อตรวจความพร้อม</span>;
}

// --- Recommendations ---

function RecommendationsTab({ jobs }) {
  // Answers "do any jobs exist at all", which the ready-to-match queue above
  // structurally cannot - see useAnyJobsExist.
  const anyJobs = useAnyJobsExist();
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    if (!selectedJobId && jobs.items.length > 0) {
      setSelectedJobId(jobs.items[0].jobId);
    }
  }, [jobs.items, selectedJobId]);

  if (jobs.status === "loading") {
    return <p className="wx-mw-muted">กำลังโหลดงานที่เปิดรับ...</p>;
  }
  if (jobs.status === "error") {
    return (
      <EmptyState
        title="โหลดงานที่เปิดรับไม่สำเร็จ"
        why="ติดต่อระบบไม่ได้ หรือบัญชีของคุณไม่มีสิทธิ์เข้าถึงข้อมูลงาน"
        next="ลองรีเฟรชหน้านี้อีกครั้ง"
      />
    );
  }
  if (jobs.items.length === 0) {
    // Jobs DO exist, they are just not marked ready - by far the more common
    // case, and the one the old copy told the operator to fix by creating a
    // second job they did not need.
    if (anyJobs.status === "ready" && anyJobs.totalCount > 0) {
      return (
        <EmptyState
          title="ยังไม่มีงานที่พร้อมจับคู่"
          why={`มีงานในระบบ ${anyJobs.totalCount.toLocaleString("th-TH")} งาน แต่ยังไม่มีงานใดถูกทำเครื่องหมายว่า "พร้อมจับคู่" การแนะนำคู่จึงยังเริ่มไม่ได้`}
          next='เปิดหน้างาน เลือกงานที่ต้องการ แล้วกด "พร้อมจับคู่ (Ready for Matching)" จากนั้นกลับมาที่แท็บนี้'
          action={{ to: "/backoffice/thailand-jobs/jobs", label: "ไปหน้างาน" }}
        />
      );
    }

    return (
      <EmptyState
        title="ยังไม่มี Job Demand สำหรับจับคู่"
        why="ยังไม่มีงานที่เปิดรับแรงงานอยู่ในระบบ การแนะนำคู่จึงเริ่มไม่ได้"
        next='เปิดหน้านายจ้าง เลือกนายจ้าง ไปที่แท็บ "งาน" แล้วกด "+ สร้างงาน" พร้อมกำหนดจำนวนที่ต้องการ จากนั้นกลับมาที่แท็บนี้'
        action={{ to: "/backoffice/thailand-jobs/employers", label: "ไปหน้านายจ้าง" }}
      />
    );
  }

  return (
    <div className="wx-mw-recs">
      <aside className="wx-mw-joblist">
        <h2>งานที่ต้องการจับคู่</h2>
        {jobs.items.map((job) => {
          // requiredWorkerCount/matchedWorkerCount/remainingWorkerCount are
          // real fields on the ready-jobs response - the fill figure is the
          // server's own count, never a derived guess.
          const required = job.requiredWorkerCount ?? 0;
          const matched = job.matchedWorkerCount ?? 0;
          const fill = required > 0 ? Math.round((matched / required) * 100) : null;
          return (
            <button
              key={job.jobId}
              type="button"
              className={`wx-mw-jobpick${selectedJobId === job.jobId ? " wx-mw-jobpick--active" : ""}`}
              onClick={() => setSelectedJobId(job.jobId)}
            >
              <strong>{job.jobTitle ?? "-"}</strong>
              <span>{job.employerName ?? "-"}</span>
              <span className="wx-mw-sub">
                {job.province ?? "-"}
                {required > 0 ? ` · ต้องการ ${required.toLocaleString("th-TH")} ตำแหน่ง` : ""}
              </span>
              {fill !== null && (
                <span className="wx-mw-jobfill">
                  <span className="wx-mw-jobfill-bar">
                    <span style={{ width: `${Math.min(fill, 100)}%` }} />
                  </span>
                  <em>
                    {matched.toLocaleString("th-TH")}/{required.toLocaleString("th-TH")} ({fill}%)
                  </em>
                </span>
              )}
            </button>
          );
        })}
      </aside>

      {selectedJobId && (
        <AiMatchingPanel jobId={selectedJobId} job={jobs.items.find((job) => job.jobId === selectedJobId) ?? null} />
      )}
    </div>
  );
}

// --- Approved / Assigned ---

function AssignedTab({ approvals }) {
  if (approvals.status === "loading") {
    return <p className="wx-mw-muted">กำลังโหลดรายการที่อนุมัติแล้ว...</p>;
  }
  if (approvals.status === "error") {
    return (
      <EmptyState
        title="โหลดรายการที่อนุมัติแล้วไม่สำเร็จ"
        why="ติดต่อระบบไม่ได้ หรือคุณไม่มีสิทธิ์เข้าถึงข้อมูลชุดนี้"
        next="ลองรีเฟรชหน้านี้อีกครั้ง"
      />
    );
  }
  if (approvals.items.length === 0) {
    return (
      <EmptyState
        title="ยังไม่มีรายชื่อที่ส่งให้นายจ้าง"
        why="ยังไม่มีแรงงานคนใดถูกส่งให้นายจ้างพิจารณา"
        next='เลือกแรงงานในแท็บ Recommendations แล้วกด "ยืนยันส่งให้นายจ้าง" รายชื่อจะมาแสดงที่นี่'
      />
    );
  }

  return (
    <>
      {/* The platform sends nothing itself: there is no employer inbox, no
          e-mail and no LINE integration here. The operator copies the link and
          sends it however they already talk to that employer, so this line
          says that plainly rather than letting the tab imply the system did
          the sending. */}
      <p className="wx-mw-lede" role="note">
        คัดลอกลิงก์ประวัติแล้วส่งให้นายจ้างทาง LINE หรืออีเมล นายจ้างเปิดลิงก์เพื่อดูประวัติ
        แล้วกดรับเข้าทำงานหรือไม่รับได้เอง ระบบไม่ได้ส่งลิงก์ให้อัตโนมัติ
      </p>
      <table className="wx-mw-table">
        <thead>
          <tr>
            <th scope="col">แรงงาน</th>
            <th scope="col">งาน</th>
            <th scope="col">คะแนน</th>
            <th scope="col">คำตอบของนายจ้าง</th>
            <th scope="col">ส่งให้นายจ้างเมื่อ</th>
            <th scope="col">ลิงก์ประวัติ</th>
          </tr>
        </thead>
        <tbody>
          {approvals.items.map((item) => {
            const decision = employerDecisionOf(item);
            const workerName = item.workerFullName ?? item.fullName ?? "-";
            return (
              <tr key={item.recommendationId ?? `${item.workerId}-${item.jobId}`}>
                <td>{workerName}</td>
                <td>{item.jobTitle ?? "-"}</td>
                <td>{item.matchScore ?? item.score ?? "-"}</td>
                <td>
                  <EmployerDecisionCell item={item} decision={decision} />
                </td>
                <td>{formatSentAt(item.approvedAtUtc)}</td>
                <td>
                  {/* A link that has already been answered is spent: offering
                      to copy it again would send the employer to a page that
                      can only tell them they already replied. */}
                  {decision ? (
                    <span className="wx-mw-sub">ตอบกลับแล้ว</span>
                  ) : (
                    <CopyResumeLinkButton
                      recommendationId={item.recommendationId}
                      workerName={workerName}
                      employerLinkToken={item.employerLinkToken ?? null}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

/**
 * What the employer said, or the honest absence of an answer.
 *
 * A rejection carries the consequence beside it, because "ไม่รับ" alone leaves
 * an operator wondering whether the worker is now stuck. They are not: sending
 * a recommendation never touched their ReadyForMatch flag, so they are still in
 * step 1 and can be sent to a different job today.
 */
function EmployerDecisionCell({ item, decision }) {
  if (!decision) {
    return <span className="wx-badge wx-badge--warning">รอนายจ้างเปิดลิงก์และตอบกลับ</span>;
  }

  const accepted = decision === EMPLOYER_DECISIONS.Accepted;
  return (
    <>
      <span className={`wx-badge wx-badge--${accepted ? "success" : "danger"}`}>
        {EMPLOYER_DECISION_LABELS[decision]}
      </span>
      <span className="wx-mw-sub wx-mw-decision-note">
        {accepted ? "ไปขั้นตอน Onboard ได้" : "กลับไปขั้นที่ 1 เสนองานอื่นได้"}
      </span>
      {item.employerDecisionNote && <span className="wx-mw-sub">หมายเหตุ: {item.employerDecisionNote}</span>}
    </>
  );
}

/** The API sends UTC; an operator reads local time, never a raw ISO string. */
function formatSentAt(value) {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }
  return parsed.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Step 4: the workers the employer actually said yes to.
 *
 * WHY THIS IS NOT THE ASSIGNMENTS LIST. It used to render the assignments
 * screen, which lists dbo.WmxAssignments rows - records that exist only after
 * somebody has already been placed. That made step 4 a view of a LATER state
 * than step 3, so a worker accepted this morning appeared nowhere in the
 * pipeline that was supposed to be tracking them. An accepted recommendation is
 * still not an assignment, which is exactly why it needs a place to sit: this
 * is the queue of people waiting to be onboarded. The assignments screen keeps
 * its own route and is linked from here.
 */
function OnboardTab({ approvals }) {
  const accepted = useMemo(
    () => approvals.items.filter((item) => employerDecisionOf(item) === EMPLOYER_DECISIONS.Accepted),
    [approvals.items],
  );

  // Deliberately NOT an early return for loading/error. The assignments list
  // below reads its own endpoints and has nothing to do with the approvals
  // call - returning early here made a slow or failing approvals request hide
  // the entire assignments screen, which is the only thing on this tab that
  // was already working.
  return (
    <>
      <section className="wx-mw-onboard-queue">
        <h2 className="wx-mw-section-title">รอเตรียม onboard</h2>
        {approvals.status === "loading" ? (
          <p className="wx-mw-muted">กำลังโหลดรายชื่อที่นายจ้างรับแล้ว...</p>
        ) : approvals.status === "error" ? (
          <EmptyState
            title="โหลดรายชื่อไม่สำเร็จ"
            why="ติดต่อระบบไม่ได้ หรือคุณไม่มีสิทธิ์เข้าถึงข้อมูลชุดนี้"
            next="ลองรีเฟรชหน้านี้อีกครั้ง"
          />
        ) : accepted.length === 0 ? (
          <EmptyState
            title="ยังไม่มีแรงงานที่นายจ้างรับเข้าทำงาน"
            why="ยังไม่มีนายจ้างรายใดกดรับเข้าทำงานจากลิงก์ประวัติที่ส่งไป"
            next='ดูรายชื่อที่รอคำตอบได้ที่แท็บ "ยืนยันโดยนายจ้าง"'
          />
        ) : (
          <>
            <p className="wx-mw-lede" role="note">
              นายจ้างรับเข้าทำงานแล้ว ขั้นถัดไปคือสร้าง Assignment แล้วผูกอุปกรณ์และ SIM
              การกดรับของนายจ้างไม่ได้สร้าง Assignment ให้อัตโนมัติ ต้องสร้างเองที่รายการด้านล่าง
            </p>
            <table className="wx-mw-table">
              <thead>
                <tr>
                  <th scope="col">แรงงาน</th>
                  <th scope="col">งาน</th>
                  <th scope="col">นายจ้าง</th>
                  <th scope="col">นายจ้างรับเมื่อ</th>
                </tr>
              </thead>
              <tbody>
                {accepted.map((item) => (
                  <tr key={item.recommendationId ?? `${item.workerId}-${item.jobId}`}>
                    <td>{item.workerFullName ?? item.fullName ?? "-"}</td>
                    <td>{item.jobTitle ?? "-"}</td>
                    <td>{item.employerName ?? "-"}</td>
                    <td>{formatSentAt(item.employerDecidedAtUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {/* The real assignments screen, minus its app shell. It stays on this tab
          because this workspace is its only route - moving it out would delete
          the only way into the assignments list. It sits BELOW the accepted
          queue because it is the later state: these are people already placed,
          not people waiting to be. */}
      <section className="wx-mw-onboard-assignments">
        <h2 className="wx-mw-section-title">Assignment ที่มีอยู่</h2>
        <AssignmentsPage embedded />
      </section>
    </>
  );
}

/** Every empty state answers: what is here, why, and what to do next. */
function EmptyState({ title, why, next, action }) {
  return (
    <div className="wx-mw-empty">
      <h2>{title}</h2>
      <p>{why}</p>
      <p className="wx-mw-empty-next">{next}</p>
      {action && (
        <Link className="wx-button wx-button--ghost" to={action.to}>
          {action.label}
        </Link>
      )}
    </div>
  );
}

export default MatchingWorkspacePage;
